import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Icon } from '@/components/ui/icon';
import { History as HistoryIcon } from 'lucide-react';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { axisStyle, seriesColors } from '@/Pages/Reports/analytics';
import { ChartTip } from '@/Pages/Reports/components';
import {
  apiError,
  ForecastTarget,
  type CurrentForecast,
  type ForecastHealth,
  type ForecastPoint,
} from './lib';

/** Money for the one target that IS money; every other target is a plain count/share. */
function isMoneyTarget(target: ForecastTarget): boolean {
  return target === ForecastTarget.SALES;
}

export default function ForecastChart({
  branchId,
  target,
  health,
}: {
  branchId: string;
  target: ForecastTarget;
  health: ForecastHealth | null;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { format: formatMoney } = useCurrency(branchId);
  const [triggering, setTriggering] = useState(false);

  const { data, loading, error, reload } = useApi<CurrentForecast>('/forecasting/points', {
    branchId,
    target,
  });

  // SALES also carries a daypart split (5 rows/day) alongside the whole-day
  // point — see ForecastGenerationService.runSales. The chart plots the
  // whole-day series; the daypart split is not this pass's chart.
  const dailyPoints = useMemo(
    () => (data?.points ?? []).filter((p) => p.daypart === null),
    [data],
  );

  const formatValue = (value: string | null): number =>
    value === null ? 0 : Number(value);

  const format = (value: string | null): string => {
    if (value === null) return '—';
    return isMoneyTarget(target)
      ? formatMoney(value)
      : new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 1 }).format(
          Number(value),
        );
  };

  const triggerRun = async () => {
    setTriggering(true);
    try {
      await api.post('/forecasting/runs', { branchId, targets: [target] });
      toast.success(t('forecasting.chart.triggered'));
      reload();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.chart.triggerFailed')));
    } finally {
      setTriggering(false);
    }
  };

  const chartData = dailyPoints.map((p) => ({
    date: p.date,
    predicted: formatValue(p.predictedValue),
    baseline: p.baselineValue === null ? null : formatValue(p.baselineValue),
    range:
      p.lowerBound !== null && p.upperBound !== null
        ? [formatValue(p.lowerBound), formatValue(p.upperBound)]
        : undefined,
    point: p,
  }));

  const dayLabel = (value: string) =>
    new Intl.DateTimeFormat(i18n.language || 'en', { month: 'short', day: 'numeric' }).format(
      new Date(`${value}T00:00:00`),
    );

  const endpoint = chartData[0];

  const canManage = can('forecasting.manage');

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{t('forecasting.chart.title')}</CardTitle>
          <p className="text-sm text-ink-muted">
            {t(`forecasting.target.${target}`)} · {t('forecasting.chart.subtitle')}
          </p>
        </div>
        {canManage && data?.run && (
          <Button variant="outline" size="sm" onClick={() => void triggerRun()} disabled={triggering}>
            {t('forecasting.chart.refresh')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={t('forecasting.chart.loadFailed')} onRetry={reload} />
        ) : !data?.run ? (
          health && !health.hasTradingHistory ? (
            // ADR-0031 §2.6 — a fresh branch with no trading history at all.
            // This is the correct screen, not an empty chart pretending
            // there is simply nothing to show today.
            <EmptyState
              title={t('forecasting.chart.insufficientHistory')}
              hint={t('forecasting.chart.insufficientHistoryHint')}
              action={
                <span className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                  <Icon as={HistoryIcon} size="sm" />
                  {t('forecasting.chart.insufficientHistoryFootnote')}
                </span>
              }
            />
          ) : (
            <EmptyState
              title={t('forecasting.chart.noRun')}
              hint={t('forecasting.chart.noRunHint')}
              action={
                canManage ? (
                  <Button size="sm" onClick={() => void triggerRun()} disabled={triggering}>
                    {t('forecasting.chart.generateNow')}
                  </Button>
                ) : undefined
              }
            />
          )
        ) : dailyPoints.length === 0 ? (
          <EmptyState
            title={t('forecasting.chart.noPoints')}
            hint={t('forecasting.chart.noPointsHint')}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-xs">
              <Legend swatch={seriesColors[0]} label={t('forecasting.chart.legendForecast')} />
              <Legend swatch={seriesColors[1]} dashed label={t('forecasting.chart.legendBaseline')} />
            </div>

            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 12, right: 12, bottom: 0, left: 8 }}>
                  <defs>
                    <linearGradient id="forecastFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={seriesColors[0]} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={seriesColors[0]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={axisStyle.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={axisStyle.tick}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => dayLabel(value as string)}
                  />
                  <YAxis
                    tick={axisStyle.tick}
                    tickLine={false}
                    axisLine={false}
                    width={64}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat(i18n.language || 'en', {
                        notation: 'compact',
                      }).format(value as number)
                    }
                  />
                  <Tooltip
                    cursor={{ stroke: axisStyle.grid }}
                    content={
                      <ChartTip
                        formatLabel={(label) => dayLabel(label as string)}
                        formatValue={(key, value) => {
                          if (key === 'predicted') return format(String(value ?? ''));
                          if (key === 'baseline')
                            return value === undefined || value === null
                              ? t('forecasting.chart.noBaseline')
                              : format(String(value));
                          return String(value ?? '');
                        }}
                      />
                    }
                  />
                  {/* Confidence band — a subtle wash behind the forecast, never a second axis. */}
                  <Area
                    type="monotone"
                    dataKey="range"
                    stroke="none"
                    fill={seriesColors[0]}
                    fillOpacity={0.08}
                    isAnimationActive={false}
                    legendType="none"
                    tooltipType="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="predicted"
                    name={t('forecasting.chart.legendForecast')}
                    stroke={seriesColors[0]}
                    strokeWidth={2}
                    fill="url(#forecastFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    name={t('forecasting.chart.legendBaseline')}
                    stroke={seriesColors[1]}
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                  {/* The emphasised endpoint: tomorrow's forecast, the nearest and
                      most actionable point on a horizon that gets less certain the
                      further out it goes. */}
                  {endpoint && (
                    <ReferenceDot
                      x={endpoint.date}
                      y={endpoint.predicted}
                      r={5}
                      fill={seriesColors[0]}
                      stroke="var(--surface)"
                      strokeWidth={2}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <PointsTable points={dailyPoints} format={format} locale={i18n.language} dayLabel={dayLabel} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Legend({ swatch, label, dashed }: { swatch: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="inline-block h-0.5 w-4 rounded-full"
        style={{
          background: dashed
            ? `repeating-linear-gradient(to right, ${swatch} 0 4px, transparent 4px 7px)`
            : swatch,
        }}
        aria-hidden
      />
      <span className="text-ink-muted">{label}</span>
    </span>
  );
}

/**
 * Every point's own `assumptions` sentence, alongside its confidence — this is
 * the honesty ADR-0031 asks for: a café manager can read WHY a number says
 * what it says, not only what the number is.
 */
function PointsTable({
  points,
  format,
  locale,
  dayLabel,
}: {
  points: ForecastPoint[];
  format: (value: string | null) => string;
  locale: string;
  dayLabel: (value: string) => string;
}) {
  const { t } = useTranslation();
  const pct = new Intl.NumberFormat(locale || 'en', { style: 'percent', maximumFractionDigits: 0 });

  return (
    <TableWrap>
      <Table>
        <THead>
          <TR>
            <TH>{t('forecasting.chart.date')}</TH>
            <TH>{t('forecasting.chart.forecast')}</TH>
            <TH>{t('forecasting.chart.legendBaseline')}</TH>
            <TH>{t('forecasting.chart.confidence')}</TH>
            <TH>{t('forecasting.chart.assumptions')}</TH>
          </TR>
        </THead>
        <tbody>
          {points.map((p) => (
            <TR key={p.id}>
              <TD className="whitespace-nowrap">{dayLabel(p.date)}</TD>
              <TD className="tabular-nums font-medium">{format(p.predictedValue)}</TD>
              <TD className="tabular-nums text-ink-muted">
                {p.baselineValue === null ? '—' : format(p.baselineValue)}
              </TD>
              <TD className="tabular-nums">{pct.format(Number(p.confidence))}</TD>
              <TD className="max-w-md text-sm text-ink-muted">{p.assumptions}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
